import React, { useEffect, useState } from "react";
import "../../App.css";
import { useNavigate } from "react-router-dom";
import BACKEND_URL from "../../config/config";
import {
  getRuntimeFeatureFlags,
  persistFeatureFlags,
  resolveFeatureFlags,
} from "../../config/featureFlags";
import EconoJournalLogo from "../../components/EconoJournalLogo";

// =======================
// CALENDAR CONFIG
// =======================
const ENABLE_CALENDAR = process.env.REACT_APP_ENABLE_CALENDAR === "true";

const EVENT = {
  title: process.env.REACT_APP_EVENT_TITLE,
  start: process.env.REACT_APP_EVENT_START_ISO,
  end: process.env.REACT_APP_EVENT_END_ISO,
  tz: process.env.REACT_APP_EVENT_TZ,
  location: process.env.REACT_APP_EVENT_LOCATION,
  url: process.env.REACT_APP_EVENT_URL,
};

// YYYYMMDDTHHmmss
const toGoogleDate = (iso) => iso.replace(/[-:]/g, "").split(".")[0];

// =======================
// CALENDAR HELPERS
// =======================
const getGoogleCalendarUrl = () => {
  if (!EVENT.start || !EVENT.end) return "#";

  const start = toGoogleDate(EVENT.start);
  const end = toGoogleDate(EVENT.end);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: EVENT.title,
    dates: `${start}/${end}`,
    details: EVENT.url,
    location: EVENT.location,
    ctz: EVENT.tz,
  });

  return `https://www.google.com/calendar/render?${params.toString()}`;
};

const downloadICS = () => {
  if (!EVENT.start || !EVENT.end) return;

  const content = `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ECJ//Evento//ES
BEGIN:VEVENT
UID:${Date.now()}@ecj
DTSTAMP:${toGoogleDate(new Date().toISOString())}Z
DTSTART:${toGoogleDate(EVENT.start)}
DTEND:${toGoogleDate(EVENT.end)}
SUMMARY:${EVENT.title}
LOCATION:${EVENT.location}
DESCRIPTION:${EVENT.url}
END:VEVENT
END:VCALENDAR
`.trim();

  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "ECJ-2026.ics";
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function Gracias() {
  const navigate = useNavigate();
  const [runtimeFlags, setRuntimeFlags] = useState(getRuntimeFeatureFlags);
  const { IS_FREE, ENABLE_EMISSION } = runtimeFlags;

  const [userId] = useState(() => {
    return (
      sessionStorage.getItem("user_id") ||
      localStorage.getItem("user_id") ||
      ""
    );
  });

  const [emissionsKg, setEmissionsKg] = useState(null);
  const [loadingEmissions, setLoadingEmissions] = useState(false);

  const [paypalStatus, setPaypalStatus] = useState(null); // "approved" | "pending" | "error" | null

  useEffect(() => {
    let cancelled = false;

    const loadPublicConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/public-config`);
        if (!res.ok) throw new Error("No se pudo cargar public-config");

        const data = await res.json();
        if (cancelled) return;

        persistFeatureFlags(data);
        setRuntimeFlags(resolveFeatureFlags(data));
      } catch (error) {
        console.error("Error cargando public-config:", error);
      }
    };

    loadPublicConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const confirmIfNeeded = async () => {
      const uid =
        sessionStorage.getItem("user_id") ||
        localStorage.getItem("user_id");

      const codeIsValid =
        (localStorage.getItem("code_is_valid") || "false") === "true";

      if (!uid) return;

      const flagKey = `thanks_confirmed_${uid}`;
      if (localStorage.getItem(flagKey) === "true") return;

      const params = new URLSearchParams(window.location.search);
      const paypalToken = params.get("token");

      try {
        if (paypalToken && !IS_FREE && !codeIsValid) {
          const res = await fetch(`${BACKEND_URL}/paypal/capture`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              order_id: paypalToken,
              id_user_response: Number(uid),
            }),
          });

          const json = await res.json();
          console.log("✅ PayPal capture response:", json);

          if (!cancelled) setPaypalStatus(json?.status || "pending");
        }

        if (!IS_FREE && codeIsValid) {
          await fetch(`${BACKEND_URL}/confirm-invited`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_user_response: Number(uid) }),
          });
        }

        localStorage.setItem(flagKey, "true");
      } catch (e) {
        console.error("❌ Error confirmando Thanks:", e);
        if (!cancelled && paypalToken) setPaypalStatus("error");
      }
    };

    const run = async () => {
      await confirmIfNeeded();
      if (cancelled) return;

      if (window.location.search.includes("token=")) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      if (!ENABLE_EMISSION) {
        localStorage.removeItem("emissions_kg_co2e");
        setEmissionsKg(0);
      } else {
        const cached = localStorage.getItem("emissions_kg_co2e");
        if (cached !== null) {
          const n = Number(cached);
          if (Number.isFinite(n)) setEmissionsKg(n);
          else localStorage.removeItem("emissions_kg_co2e");
        }

        if ((!cached || !Number.isFinite(Number(cached))) && userId) {
          setLoadingEmissions(true);
          try {
            const r = await fetch(`${BACKEND_URL}/emissions/${encodeURIComponent(userId)}`);
            if (!r.ok) throw new Error("emissions fetch failed");
            const d = await r.json();

            const co2 =
              d?.emissions_kg_co2e ??
              d?.co2_total_per_user ??
              d?.co2_emitted_user;
            const co2Number = Number(co2);

            if (Number.isFinite(co2Number)) {
              setEmissionsKg(co2Number);
              localStorage.setItem("emissions_kg_co2e", String(co2Number));
              sessionStorage.setItem("co2_total_per_user", String(co2Number));
            } else {
              setEmissionsKg(0);
            }
          } catch (e) {
            setEmissionsKg(0);
          } finally {
            setLoadingEmissions(false);
          }
        }
      }

      window.scrollTo(0, 0);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [userId, IS_FREE, ENABLE_EMISSION]);

  const btnBackToHome = () => {
    localStorage.setItem("flow_completed", "true");

    sessionStorage.clear();

    localStorage.removeItem("code_is_valid");
    localStorage.removeItem("email");
    localStorage.removeItem("final_price");
    localStorage.removeItem("currency");
    localStorage.removeItem("user_id");
    localStorage.removeItem("company_name");
    localStorage.removeItem("id_company");

    navigate("/");
  };

  const calendarSectionStyle = {
    marginTop: 40,
    padding: 20,
    border: "1px solid var(--border-color, #C9C5CA)",
    borderRadius: 8,
    backgroundColor: "var(--card-bg, transparent)",
    color: "var(--text-color, inherit)",
    textAlign: "center",
  };

  const calendarButtonsStyle = {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    marginTop: 16,
    flexWrap: "wrap",
  };

  const calendarBtnBase = {
    minWidth: 220,
    padding: "12px 18px",
    borderRadius: 8,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const googleBtnStyle = { ...calendarBtnBase, background: "#4285f4", color: "white" };

  return (
    <div className="thanks">
      <div>
        <EconoJournalLogo className="logoECJ" />
        <h1>¡Gracias por sumarte!</h1>

        {paypalStatus === "pending" && <h3>Estamos confirmando tu pago de PayPal...</h3>}
        {paypalStatus === "error" && <h3>Hubo un problema confirmando el pago. Si ya pagaste, actualizá la página.</h3>}

        <h3>Tu inscripción está confirmada.</h3>

        {ENABLE_CALENDAR && (
          <div className="calendar-section" style={calendarSectionStyle}>
            <h3>Guardá la fecha</h3>

            <div className="calendar-buttons" style={calendarButtonsStyle}>
              <a
                href={getGoogleCalendarUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-calendar google"
                style={googleBtnStyle}
              >
                Agregar a Google Calendar
              </a>
            </div>
          </div>
        )}
      </div>

      <div
        className="sectionUserId"
        style={{ textAlign: "left", width: "100%", marginTop: 24 }}
      >
        <h3 style={{ marginBottom: 8 }}>Código de registro:</h3>
        <h1
          className="idUser"
          style={{
            marginTop: 0,
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "left",
          }}
        >
          {userId ? `${userId}` : "#No disponible"}
        </h1>

      </div>


      {ENABLE_EMISSION && (
        <>
          <div className="sectionEmissions">
            <h3>Tus emisiones:</h3>
            <h2 className="idUser">
              {loadingEmissions
                ? "Calculando..."
                : emissionsKg != null
                  ? `${emissionsKg.toLocaleString("en-US", { maximumFractionDigits: 2 })} kg CO₂e`
                  : "0"}
            </h2>
          </div>

          <h3>Medir es el primer paso para mejorar.</h3>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "start", marginTop: 24 }}>
        <button
          type="button"
          onClick={btnBackToHome}
        >
          Volver al inicio
        </button>
      </div>


    </div>
  );
}
